// src/HoursSettings.js
import React, { useState, useEffect } from 'react';
import TitleBar from './components/ui/TitleBar';
import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';

const API_BASE = getApiBase();

const getIsMobile = () => typeof window !== 'undefined' && window.innerWidth < 1024;

const HoursSettings = ({ navigate, isPhone }) => {
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const defaultSettings = {
    monday:    { open: '9:00 AM', close: '5:00 PM', closed: false },
    tuesday:   { open: '9:00 AM', close: '5:00 PM', closed: false },
    wednesday: { open: '9:00 AM', close: '5:00 PM', closed: false },
    thursday:  { open: '9:00 AM', close: '5:00 PM', closed: false },
    friday:    { open: '9:00 AM', close: '5:00 PM', closed: false },
    saturday:  { open: '9:00 AM', close: '5:00 PM', closed: true },
    sunday:    { open: '9:00 AM', close: '5:00 PM', closed: true },
    blockoutPercentage: 15
  };

  const [settings, setSettings] = useState(defaultSettings);

  // ── Proper time options generator ──────────────────────────────────────────
  const generateTimeOptions = () => {
    const times = [];
    for (let totalMinutes = 6 * 60; totalMinutes <= 22 * 60; totalMinutes += 30) {
      const hour24 = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      const period = hour24 >= 12 ? 'PM' : 'AM';
      let hour12 = hour24 % 12;
      if (hour12 === 0) hour12 = 12;
      times.push(`${hour12}:${minutes.toString().padStart(2, '0')} ${period}`);
    }
    return times;
  };

  const timeOptions = generateTimeOptions();
  const blockoutOptions = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50];

  // ── Responsive listener ────────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => setIsMobile(getIsMobile());
    window.addEventListener('resize', onResize);
    setIsMobile(getIsMobile());
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Time conversion helpers ────────────────────────────────────────────────
  const convertTo12Hour = (time24) => {
    if (!time24 || !time24.includes(':')) return '9:00 AM';
    const [hourStr, minuteStr] = time24.split(':');
    let hour = parseInt(hourStr, 10);
    const minutes = minuteStr ? minuteStr.slice(0, 2) : '00';
    if (isNaN(hour)) return '9:00 AM';
    const period = hour >= 12 ? 'PM' : 'AM';
    if (hour === 0) hour = 12;
    else if (hour > 12) hour -= 12;
    return `${hour}:${minutes} ${period}`;
  };

  const convertTo24Hour = (time12) => {
    if (!time12 || !time12.includes(' ')) return '09:00';
    const parts = time12.split(' ');
    const period = parts[1];
    const timeParts = parts[0].split(':');
    let hour = parseInt(timeParts[0], 10);
    const minutes = timeParts[1] || '00';
    if (isNaN(hour)) return '09:00';
    if (period === 'PM' && hour !== 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;
    return `${hour.toString().padStart(2, '0')}:${minutes}`;
  };

  // ── Load settings ──────────────────────────────────────────────────────────
  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/hours`, {
        headers: { ...getTenantHeaders() }
      });

      if (res.ok) {
        const data = await res.json();
        if (data && Object.keys(data).length > 0) {
          const newSettings = { ...defaultSettings };
          const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

          days.forEach(day => {
            if (data[day]) {
              const val = String(data[day]);
              if (val.toLowerCase() === 'closed') {
                newSettings[day] = { ...newSettings[day], closed: true };
              } else if (val.includes('-')) {
                const [open, close] = val.split('-');
                newSettings[day] = {
                  open: convertTo12Hour(open.trim()),
                  close: convertTo12Hour(close.trim()),
                  closed: false
                };
              }
            }
          });

          // FIX: read bufferPercentage from DB (was incorrectly reading blockoutPercentage)
          const pct = parseInt(data.bufferPercentage, 10);
          newSettings.blockoutPercentage = Number.isFinite(pct) ? pct : 15;

          setSettings(newSettings);
        }
      }
    } catch (error) {
      console.error('Failed to load hours settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Save settings ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      const serverData = {};

      days.forEach(day => {
        serverData[day] = settings[day].closed
          ? 'Closed'
          : `${convertTo24Hour(settings[day].open)}-${convertTo24Hour(settings[day].close)}`;
      });

      // FIX: send as bufferPercentage to match DB column name (was sending blockoutPercentage)
      serverData.bufferPercentage = parseInt(String(settings.blockoutPercentage), 10);

      const res = await fetch(`${API_BASE}/api/settings/hours`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
        body: JSON.stringify(serverData)
      });

      if (!res.ok) throw new Error('Save failed');
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'HOURS SETTINGS SAVED!' }
      }));
      await loadSettings();
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save settings: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const updateDay = (day, field, value) => {
    setSettings(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
  };

  // ── Styles ─────────────────────────────────────────────────────────────────
  const styles = {
    page: {
      padding: isPhone ? '18px 4px' : (isMobile ? '18px' : '40px'),
      backgroundColor: '#E8E2DA',
      minHeight: '100vh'
    },
    contentWrapper: {
      maxWidth: '1200px',
      padding: isPhone ? '0' : '0 12px',
      margin: '0 auto'
    },
    panel: {
      backgroundColor: '#E7B888',
      border: isMobile ? '6px solid black' : '10px solid black',
      padding: isPhone ? '20px 5px' : (isMobile ? '20px' : '40px'),
      marginBottom: '20px'
    },
    label: {
      fontWeight: 900,
      textTransform: 'uppercase',
      fontSize: '14px',
      marginBottom: '8px',
      display: 'block'
    },
    select: {
      padding: '14px',
      border: '4px solid black',
      fontWeight: 700,
      width: '100%',
      fontSize: '16px',
      boxSizing: 'border-box',
      backgroundColor: 'white'
    },
    dayRow: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '150px 1fr 1fr auto',
      gap: '12px',
      marginBottom: '16px',
      alignItems: 'center'
    },
    dayLabel: {
      fontWeight: 900,
      textTransform: 'uppercase',
      fontSize: isMobile ? '14px' : '16px',
      padding: isMobile ? '8px 0' : '0'
    },
    closedBlock: {
      gridColumn: isMobile ? '1' : '2 / 4',
      padding: '14px',
      border: '4px solid black',
      backgroundColor: '#f0f0f0',
      fontWeight: 900
    },
    checkbox: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: 900,
      fontSize: '14px',
      padding: '14px',
      border: '4px solid black',
      backgroundColor: 'white',
      cursor: 'pointer',
      whiteSpace: 'nowrap'
    },
    saveBtn: {
      background: 'black',
      color: '#FFA500',
      border: '4px solid #E8761D',
      padding: '16px 32px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '18px',
      width: isMobile ? '100%' : 'auto'
    },
    note: {
      fontWeight: 900,
      fontSize: '13px',
      padding: '12px',
      border: '3px solid black',
      backgroundColor: '#ffffcc',
      marginBottom: '20px'
    },
    blockoutSection: {
      marginTop: '30px',
      padding: '20px',
      border: '4px solid black',
      backgroundColor: '#f0f0f0'
    },
    loadingBox: {
      padding: '30px',
      textAlign: 'center',
      fontWeight: 900,
      fontSize: '16px'
    }
  };

  const days = [
    { key: 'monday',    label: 'MONDAY' },
    { key: 'tuesday',   label: 'TUESDAY' },
    { key: 'wednesday', label: 'WEDNESDAY' },
    { key: 'thursday',  label: 'THURSDAY' },
    { key: 'friday',    label: 'FRIDAY' },
    { key: 'saturday',  label: 'SATURDAY' },
    { key: 'sunday',    label: 'SUNDAY' }
  ];

  return (
    <div style={styles.page}>
      <style>{`
        .jf-hours-hover:hover {
          background-color: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
          transition: all 0.12s ease;
        }
        .jf-hours-hover:active {
          transform: translateY(1px);
        }
        @media (max-width: 1023px) {
          .hours-day-row {
            display: flex !important;
            flex-direction: column !important;
            gap: 10px !important;
          }
          .hours-closed-block {
            width: 100% !important;
          }
        }
      `}</style>

      <div style={styles.contentWrapper}>
        <TitleBar
          icon="🕐"
          title="HOURS SETTINGS"
          accent="#E8761D"
          right={
            <button
              className="jf-hours-hover"
              onClick={() => navigate('/settings')}
              style={{
                background: 'black',
                color: '#FFA500',
                border: '5px solid #E8761D',
                padding: '14px',
                fontWeight: 900,
                cursor: 'pointer',
                textTransform: 'uppercase'
              }}
            >
              BACK TO SETTINGS
            </button>
          }
        />

        <div style={styles.panel}>
          {loading ? (
            <div style={styles.loadingBox}>⏳ LOADING HOURS SETTINGS...</div>
          ) : (
            <>
              <div style={styles.note}>
                Set your weekly operating hours. These will be used for online booking availability. Blockout percentage hides time slots from ONLINE CUSTOMERS ONLY — you still see all available times.
              </div>

              {days.map(day => (
                <div key={day.key} style={styles.dayRow} className="hours-day-row">
                  <div style={styles.dayLabel}>{day.label}</div>

                  {settings[day.key].closed ? (
                    <div style={styles.closedBlock} className="hours-closed-block">
                      CLOSED
                    </div>
                  ) : (
                    <>
                      <select
                        style={styles.select}
                        value={settings[day.key].open}
                        onChange={e => updateDay(day.key, 'open', e.target.value)}
                      >
                        {timeOptions.map(time => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>

                      <select
                        style={styles.select}
                        value={settings[day.key].close}
                        onChange={e => updateDay(day.key, 'close', e.target.value)}
                      >
                        {timeOptions.map(time => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </>
                  )}

                  <label style={styles.checkbox}>
                    <input
                      type="checkbox"
                      checked={settings[day.key].closed}
                      onChange={e => updateDay(day.key, 'closed', e.target.checked)}
                      style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                    />
                    CLOSED
                  </label>
                </div>
              ))}

              <div style={styles.blockoutSection}>
                <label style={styles.label}>CALENDAR BLOCKOUT PERCENTAGE (ONLINE BOOKING ONLY)</label>
                <p style={{ fontSize: '13px', marginBottom: '12px', fontWeight: 700 }}>
                  Hides time slots from online customers only. You still see all available times. Makes you appear busier and in higher demand. 0% = show all slots to customers.
                </p>
                <select
                  style={styles.select}
                  value={String(settings.blockoutPercentage)}
                  onChange={e => setSettings(prev => ({ ...prev, blockoutPercentage: parseInt(e.target.value, 10) }))}
                >
                  {blockoutOptions.map(percent => (
                    <option key={percent} value={String(percent)}>
                      {percent}%{percent === 0 ? ' (Show All Slots)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginTop: '30px' }}>
                <button
                  className="jf-hours-hover"
                  style={styles.saveBtn}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'SAVING...' : 'SAVE HOURS SETTINGS'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HoursSettings;
